import { linkOptions, type LinkProps } from '@tanstack/react-router'

import type { NavFamily } from '@/components/layout/nav-items'
import { resolveFamily, withinPrefix } from '@/components/layout/use-active-section'
import type { SoulName } from '@/components/ui/soul-icons'
import { config } from '@/lib/config'
import type { Audience } from '@/schemas/baskets'
import type { IssueListType } from '@/schemas/ipo'

/**
 * The sidebar's sections (nav revamp, owner "okay" 2026-10-14; order and
 * stock placement re-ruled 2026-10-23; grouped to the revamp mock,
 * 2026-10-26): ONE global accordion — Home, then Research (Market · Broker
 * Analysis · Mutual Funds · Signal Zone · Public Issues), Your Money
 * (Portfolio · Alerts) and Tools (Mitra AI · Analytics Studio · Screener ·
 * Baskets · Technical Chart · Learn) — with a stock's own section right under
 * Market, and an Account or a Watchlist page's own section on top. Every page
 * of the app lives under its section; the old Explore flyout and the
 * one-context-group rail are gone.
 *
 * Built by a PURE function of the URL (plus, for Watchlist and Alerts, the
 * data their rows are made of): the same `NavSection` shape out of every
 * builder, one renderer (SidebarSections.tsx) for all of them, and nothing
 * about a section living anywhere but its builder below.
 */

/** The two halves of the URL a row reads: the path, and the parsed search. */
export interface NavLocation {
  readonly pathname: string
  readonly search: Readonly<Record<string, unknown>>
}

/*
 * Where a row goes. Every link below is built by `linkOptions`, which checks
 * its path, its params and each search value against the route tree right
 * where it is written — `{ to: '/settings', search: { tab: 'securtiy' } }` is
 * a compile error, not a dead row.
 *
 * The parametrised ones go through one helper per route, and `NavLink` is the
 * union of exactly those shapes. It is NOT widened to TanStack's `LinkOptions`:
 * that type merges every route's search keys into one object, so it rejects a
 * key only one route declares (`?list=`) and a value two routes spell
 * differently (`?tab=` names a stock section on `/stocks/$symbol` and a pane
 * on `/settings`). A plain path is validated where it is written
 * and stored as just its path.
 */

type RoutePath = NonNullable<LinkProps['to']>

const stockLink = (symbol: string, tab: StockTab) =>
  linkOptions({ to: '/stocks/$symbol', params: { symbol }, search: { tab } })

const settingsLink = (tab: SettingsTab) => linkOptions({ to: '/settings', search: { tab } })

const watchlistLink = (list: string) => linkOptions({ to: '/watchlist', search: { list } })

const alertsLink = (status: AlertStatus) => linkOptions({ to: '/alerts', search: { status } })

const issuesLink = (type: IssueListType) => linkOptions({ to: '/market/issues', search: { type } })

const brokerLink = (code: string) => linkOptions({ to: '/brokers/$code', params: { code } })

const brokerReviewsLink = (code: string) =>
  linkOptions({ to: '/brokers/$code/reviews', params: { code } })

const brokerStockLink = (symbol: string) =>
  linkOptions({ to: '/brokers/by-stock/$symbol', params: { symbol } })

const pvRangeLink = (symbol: string) =>
  linkOptions({ to: '/brokers/pv-range/$symbol', params: { symbol } })

export type NavLink =
  | { readonly to: RoutePath }
  | ReturnType<typeof stockLink>
  | ReturnType<typeof settingsLink>
  | ReturnType<typeof watchlistLink>
  | ReturnType<typeof alertsLink>
  | ReturnType<typeof issuesLink>
  | ReturnType<typeof brokerLink>
  | ReturnType<typeof brokerReviewsLink>
  | ReturnType<typeof brokerStockLink>
  | ReturnType<typeof pvRangeLink>

interface RowBase {
  /** Stable within its section — the React key. */
  readonly key: string
  readonly label: string
  /**
   * The illustrated soul before the label; the art per row is the one the
   * family's frames draw (e.g. Floorsheet is `soul/table` on `5199:59365`).
   */
  readonly icon: SoulName
}

/** A page row that goes somewhere. */
export interface LinkRow extends RowBase {
  /** Printed after the label as "· N": Alerts' live counts. */
  readonly count?: number
  readonly link: NavLink
  /** Lit: its path AND its own search key name the page on screen. */
  readonly active: boolean
  readonly soon?: never
}

/**
 * A page we have not built (owner rule, 2026-10-27): drawn muted with a
 * "Soon" tag, `aria-disabled`, and no link or route behind it — the reason
 * it has no `link` to follow. Never lit.
 */
export interface SoonRow extends RowBase {
  readonly soon: true
  readonly active: false
}

/** One page row inside an open section, and one tab of its strip. */
export type PageRow = LinkRow | SoonRow

/**
 * A section's pages as its frame draws them: the first few, then "Show N
 * more" opening the rest inline (owner, 2026-10-12, LOCKED: every section
 * with more pages than its frame shows keeps the split). `more` is empty for
 * a section whose frame draws every page.
 */
export interface Paged<T> {
  readonly visible: readonly T[]
  readonly more: readonly T[]
}

/**
 * Every section the sidebar can draw. The families (nav-items.ts) plus the
 * two rows no page of the shell belongs to: Technical Chart, whose workspace
 * lives outside the shell and opens in a tab of its own, and Mitra AI, which
 * opens the Mitra panel.
 */
export type NavSectionId = NavFamily | 'chart' | 'mitra' | 'commodities'

/**
 * The labelled groups the sections fall into (revamp mock, 2026-10-26),
 * drawn in this order under the ungrouped top — the page's own context
 * section and Home. A label on the full rail, a hairline on the collapsed.
 */
export const NAV_GROUPS = {
  research: 'Research',
  money: 'Your Money',
  tools: 'Tools',
} as const

export type NavGroupId = keyof typeof NAV_GROUPS

interface SectionBase {
  readonly id: NavSectionId
  readonly label: string
  readonly icon: SoulName
  /** The page's own section (a stock, Account, Watchlist), drawn first while it is open. */
  readonly context?: true
  /** The group it is drawn in; none for the ungrouped top (context, Home). */
  readonly group?: NavGroupId
}

interface SectionHead extends SectionBase {
  /** Where the head goes: the section's front page. */
  readonly front: NavLink
  /** The page on screen is this section's: a link row lights, a head turns white. */
  readonly current: boolean
}

/**
 * - `link`: a plain row with no pages (Home, Analytics Studio, Technical Chart).
 * - `pages`: a head, a chevron, and its pages.
 * - `alerts` / `watchlist`: the same, but the rows are the member's own data,
 *   so the renderer reads them (`alertsPages`, `watchlistPages`) only while
 *   the section is open — no request for a section nobody opened.
 * - `mitra`: Mitra AI, a button rather than a link: it opens the Mitra panel
 *   through its store, which takes the reader to `front` (`/chat`) and steps
 *   back off it on close (useMitraRoute) — a plain link to `/chat` would
 *   close to Home. It lights from the panel's own state, not the URL.
 * - `soon`: a section we have not built (Commodities — owner rule,
 *   2026-10-27): one muted row with a "Soon" tag, `aria-disabled`, no front
 *   page, no chevron and no pages, since every page under it is Soon too.
 *   Its routes still answer a typed URL; nothing here links to them, and it
 *   never lights.
 */
export type NavSection =
  | (SectionHead & { readonly kind: 'link'; readonly newTab?: boolean })
  | (SectionHead & { readonly kind: 'pages'; readonly pages: Paged<PageRow> })
  | (SectionHead & { readonly kind: 'alerts' | 'watchlist' | 'mitra' })
  | (SectionBase & { readonly kind: 'soon'; readonly current: false })

/** A value that arrives from a query: still coming, failed, or here. */
export type Loadable<T> =
  | { readonly status: 'pending' }
  | { readonly status: 'failed' }
  | { readonly status: 'ready'; readonly data: T }

type PageSpec = Omit<LinkRow, 'active'>

const lit = (row: PageSpec, active: boolean): LinkRow => ({ ...row, active })

const decodeSegment = (segment: string) => {
  try {
    return decodeURIComponent(segment)
  } catch {
    // A malformed escape is still the segment the member typed; showing it
    // raw beats throwing the whole rail.
    return segment
  }
}

const segmentsOf = (pathname: string) =>
  pathname
    .split('/')
    .filter((segment) => segment !== '')
    .map(decodeSegment)

/**
 * A search value as the string a row compares against. The router parses
 * `?list=12` into the NUMBER 12, so both shapes are read as text.
 */
const searchText = (search: NavLocation['search'], key: string): string | undefined => {
  const value = search[key]
  return typeof value === 'string' || typeof value === 'number' ? String(value) : undefined
}

/**
 * Which of `values` the page is showing: the URL's own when it names one, the
 * route's default otherwise — the fallback every one of these screens'
 * `validateSearch` applies, so a bare `/settings` or a stale `?tab=` lights
 * the row for the page actually on screen, never nothing.
 */
function currentOf<V extends string>(
  location: NavLocation,
  key: string,
  values: readonly V[],
  fallback: V,
  aliases: Readonly<Record<string, V>> = {},
): V {
  const raw = searchText(location.search, key)
  if (raw === undefined) return fallback
  const resolved = aliases[raw] ?? raw
  return values.find((value) => value === resolved) ?? fallback
}

/** A split's pages as rows, each lit by `toRow`, the split itself kept. */
const litPages = <T>(pages: Paged<T>, toRow: (page: T) => PageRow): Paged<PageRow> => ({
  visible: pages.visible.map(toRow),
  more: pages.more.map(toRow),
})

const allVisible = <T>(rows: readonly T[]): Paged<T> => ({ visible: rows, more: [] })

/**
 * The section the page on screen belongs to, or `null` where nothing lights:
 * What's Happening and Notifications (the reference lights nothing there;
 * the Shortcuts flyout and the bell reach them), and any route no family
 * claims.
 */
export function currentSectionOf(pathname: string): NavSectionId | null {
  const family = resolveFamily(pathname)
  if (family === 'home') return pathname === '/' ? 'home' : null
  return family
}

/* MARKET — `/market/*` (4236:100580). The revamp mock's
   list and split (owner D2, 2026-10-27): eight rows, then "Show 6 more".
   Gainers & Losers and Stock Prices left the list; their routes stay, for the
   Top Gainers shortcut and old links. Trading Average and Stock Trading are
   the two new pages, built from a stock's price history. Newsletters moved
   here from Learn, and lights on an issue too. Three of the old rows are
   sections of their own now: Public Issues, Signal Zone and the chart
   (Technical Chart). */

const NEWSLETTERS = '/market/newsletters'

const MARKET_PAGES: Paged<PageSpec> = {
  visible: [
    { key: '/market', icon: 'market', label: 'Overview', link: linkOptions({ to: '/market' }) },
    {
      key: '/market/movers',
      icon: 'movers',
      label: 'Movers',
      link: linkOptions({ to: '/market/movers' }),
    },
    {
      key: '/market/sectors',
      icon: 'pie',
      label: 'Sectors',
      link: linkOptions({ to: '/market/sectors' }),
    },
    {
      key: '/market/floorsheet',
      icon: 'table',
      label: 'Floorsheet',
      link: linkOptions({ to: '/market/floorsheet' }),
    },
    {
      key: '/market/events',
      icon: 'certificate',
      label: 'Events',
      link: linkOptions({ to: '/market/events' }),
    },
    {
      key: '/market/indices',
      icon: 'index',
      label: 'Indices',
      link: linkOptions({ to: '/market/indices' }),
    },
    {
      key: '/market/update',
      icon: 'news',
      label: 'Market Update',
      link: linkOptions({ to: '/market/update' }),
    },
    {
      key: '/market/depth',
      icon: 'depth',
      label: 'Market Depth',
      link: linkOptions({ to: '/market/depth' }),
    },
  ],
  more: [
    {
      key: '/market/live',
      icon: 'market',
      label: 'Live Market',
      link: linkOptions({ to: '/market/live' }),
    },
    {
      key: '/market/heatmap',
      icon: 'market',
      label: 'Market Heatmap',
      link: linkOptions({ to: '/market/heatmap' }),
    },
    {
      key: '/market/trading-average',
      icon: 'calc',
      label: 'Trading Average',
      link: linkOptions({ to: '/market/trading-average' }),
    },
    {
      key: '/market/stock-trading',
      icon: 'receipt',
      label: 'Stock Trading',
      link: linkOptions({ to: '/market/stock-trading' }),
    },
    {
      key: '/market/52-week',
      icon: 'range',
      label: '52W Change',
      link: linkOptions({ to: '/market/52-week' }),
    },
    {
      key: NEWSLETTERS,
      icon: 'mail',
      label: 'Newsletters',
      link: linkOptions({ to: '/market/newsletters' }),
    },
  ],
}

const marketRowLit = (pathname: string) => (row: PageSpec) =>
  lit(row, row.key === NEWSLETTERS ? withinPrefix(pathname, NEWSLETTERS) : row.key === pathname)

/* SIGNAL ZONE — `/market/signals/*` (owner decision D7), its own section
   right after Mutual Funds (owner, 2026-10-23). Dashboard, Live Signals, Daily
   Forecast, Trend Signals, Essentials, then "Show 5 more". "AI Essentials" is
   "Essentials" (owner ruling: a rule-based engine, no AI). A row lights on its
   path alone; the pages' sessions, sides and pages live in their search
   params. A stock's Signal page (`/market/signals/stock/$symbol`) has no row:
   it lights the board it was opened from, `?from=` (Live unless Daily), in the
   sidebar and in the section strip it keeps. */

const SIGNAL_STOCK_PREFIX = '/market/signals/stock/'

/** The signals row the page on screen lights: its own, or the Signal page's board. */
const signalsRowKey = (location: NavLocation): string =>
  location.pathname.startsWith(SIGNAL_STOCK_PREFIX)
    ? `/market/signals/${currentOf(location, 'from', ['live', 'daily'], 'live')}`
    : location.pathname

const SIGNALS_PAGES: Paged<PageSpec> = {
  visible: [
    {
      key: '/market/signals',
      icon: 'gauge',
      label: 'Dashboard',
      link: linkOptions({ to: '/market/signals' }),
    },
    {
      key: '/market/signals/live',
      icon: 'pulse',
      label: 'Live Signals',
      link: linkOptions({ to: '/market/signals/live' }),
    },
    {
      key: '/market/signals/daily',
      icon: 'clipboard',
      label: 'Daily Forecast',
      link: linkOptions({ to: '/market/signals/daily' }),
    },
    {
      key: '/market/signals/trend',
      icon: 'movers',
      label: 'Trend Signals',
      link: linkOptions({ to: '/market/signals/trend' }),
    },
    {
      key: '/market/signals/essentials',
      icon: 'discover',
      label: 'Essentials',
      link: linkOptions({ to: '/market/signals/essentials' }),
    },
  ],
  more: [
    {
      key: '/market/signals/breakouts',
      icon: 'range',
      label: 'Breakout Finder',
      link: linkOptions({ to: '/market/signals/breakouts' }),
    },
    {
      key: '/market/signals/volatile',
      icon: 'candles',
      label: 'Volatile Stocks',
      link: linkOptions({ to: '/market/signals/volatile' }),
    },
    {
      key: '/market/signals/open-trades',
      icon: 'tag',
      label: 'Open Trades',
      link: linkOptions({ to: '/market/signals/open-trades' }),
    },
    {
      key: '/market/signals/cycle-map',
      icon: 'table',
      label: 'Market Cycle Map',
      link: linkOptions({ to: '/market/signals/cycle-map' }),
    },
    {
      key: '/market/signals/history',
      icon: 'receipt',
      label: 'Signal History',
      link: linkOptions({ to: '/market/signals/history' }),
    },
  ],
}

/* STOCK — `/stocks/$symbol?tab=` (4236:73517 …), a context section right
   under Market while a stock is open (owner, 2026-10-23). Headed by the
   ticker: the six sections the frame draws, then "Show 8 more" (the More menu
   `4253:5476`, plus Dividend, Price History and Share Holding). */

const STOCK_TABS = [
  'overview',
  'financials',
  'technical',
  'floorsheet',
  'advanced',
  'events',
  'dividend',
  'share-holding',
  'price-history',
  'broker-holding',
  'agm-history',
  'announcements',
  'compare',
  'reports',
] as const

type StockTab = (typeof STOCK_TABS)[number]

/** V1's `?tab=analysis`, still in bookmarks — StockScreen reads it as Technical. */
const STOCK_TAB_ALIASES: Readonly<Record<string, StockTab>> = { analysis: 'technical' }

interface StockPage {
  readonly tab: StockTab
  readonly label: string
  readonly icon: SoulName
}

const STOCK_PAGES: Paged<StockPage> = {
  visible: [
    { tab: 'overview', label: 'Overview', icon: 'market' },
    { tab: 'financials', label: 'Financials', icon: 'database' },
    { tab: 'technical', label: 'Technical', icon: 'candles' },
    { tab: 'floorsheet', label: 'Floorsheet', icon: 'table' },
    { tab: 'advanced', label: 'Advanced', icon: 'sliders' },
    { tab: 'events', label: 'Events', icon: 'certificate' },
  ],
  more: [
    { tab: 'dividend', label: 'Dividend', icon: 'coin' },
    { tab: 'share-holding', label: 'Share Holding', icon: 'users' },
    { tab: 'price-history', label: 'Price History', icon: 'range' },
    { tab: 'broker-holding', label: 'Broker Holding', icon: 'handshake' },
    { tab: 'agm-history', label: 'AGM History', icon: 'certificate' },
    { tab: 'announcements', label: 'Announcements', icon: 'megaphone' },
    { tab: 'reports', label: 'Reports', icon: 'doc' },
    { tab: 'compare', label: 'Compare', icon: 'compare' },
  ],
}

function stockSection(location: NavLocation): NavSection | null {
  const [, symbol] = segmentsOf(location.pathname)
  if (symbol === undefined) return null

  const current = currentOf(location, 'tab', STOCK_TABS, 'overview', STOCK_TAB_ALIASES)
  return {
    kind: 'pages',
    id: 'stock',
    context: true,
    group: 'research',
    label: symbol,
    icon: 'tag',
    front: stockLink(symbol, 'overview'),
    current: true,
    pages: litPages(STOCK_PAGES, ({ tab, label, icon }) =>
      lit({ key: tab, label, icon, link: stockLink(symbol, tab) }, tab === current),
    ),
  }
}

/* LEARN — `/courses/*`, `/learn/*` (owner decision, 2026-09-26). The revamp
   mock's five rows (owner D7, 2026-10-27), headed on Objectives — the
   learning path. Newsletters moved to Market; the FAQ keeps its route but
   has no row (the list D7 approved has none).

   Courses lights on the hub and on everything under a course — its page and
   its lectures (`/courses/$courseId/…`) — but not on the pages that share
   the `/courses/` prefix and are not a course. Objectives and Dictionary
   light on their own pages and everything under them (a step, a term).
   Search params (a tab, a filter, a page) never change which row is lit. */

/** The `/courses/<segment>` pages that are not a course. */
const COURSES_OWN_PAGES: ReadonlySet<string> = new Set([
  'my-learning',
  'certificates',
  'path',
  'faq',
])

function learnPages(pathname: string): Paged<PageRow> {
  const [first, second] = segmentsOf(pathname)
  // rest:ln (owner L2) — the course questions page sits under Courses, its parent tab lit.
  const onCourse =
    first === 'courses' &&
    (second === undefined || second === 'faq' || !COURSES_OWN_PAGES.has(second))
  const row = (key: string, label: string, icon: SoulName, link: NavLink, active: boolean) =>
    lit({ key, label, icon, link }, active)

  return allVisible([
    row(
      'path',
      'Objectives',
      'index',
      linkOptions({ to: '/courses/path' }),
      withinPrefix(pathname, '/courses/path'),
    ),
    row('courses', 'Courses', 'learn', linkOptions({ to: '/courses' }), onCourse),
    row(
      'my-learning',
      'My Learning',
      'clipboard',
      linkOptions({ to: '/courses/my-learning' }),
      pathname === '/courses/my-learning',
    ),
    row(
      'certificates',
      'Certificates',
      'certificate',
      linkOptions({ to: '/courses/certificates' }),
      pathname === '/courses/certificates',
    ),
    row(
      'dictionary',
      'Dictionary',
      'dictionary',
      linkOptions({ to: '/learn/dictionary' }),
      withinPrefix(pathname, '/learn/dictionary'),
    ),
  ])
}

/**
 * rest:ln (owner L2) — Learn's drill-downs that keep the section strip, their parent tab lit, as
 * the mock draws them: a path step (Objectives), a course's own page and the course questions
 * (Courses), a dictionary word (Dictionary). A course's lectures and its checkout do not: the
 * player and the checkout are pages of their own, with no mock strip.
 */
const isLearnDrillDown = (pathname: string): boolean => {
  const [first, second, third] = segmentsOf(pathname)
  if (first === 'learn') return second === 'dictionary' && third !== undefined
  if (first !== 'courses' || second === undefined) return false
  if (second === 'path') return third !== undefined
  return third === undefined && (second === 'faq' || !COURSES_OWN_PAGES.has(second))
}

/* SCREENER — `/screener/*` (owner, 2026-10-30). The mock's six pages in its order: Advanced is
   live, the one with an API behind it; Basic, Fundamental, Technical, Dividends and Custom have
   none, so they are Soon rows (muted, `aria-disabled`, no link and no route behind them). Five
   rows, then "Show 1 more", as Mutual Funds splits its six: every sidebar group keeps More
   (owner-locked 2026-10-12). The mock's seventh row, Compare, is the stock Compare page, not a
   screener, and is left to the stock section. */

const soonRow = (key: string, label: string, icon: SoulName): SoonRow => ({
  key,
  label,
  icon,
  soon: true,
  active: false,
})

const SCREENER_VISIBLE = 5

function screenerPages(pathname: string): Paged<PageRow> {
  const rows: PageRow[] = [
    soonRow('basic', 'Basic', 'sliders'),
    soonRow('fundamental', 'Fundamental', 'database'),
    soonRow('technical', 'Technical', 'candles'),
    soonRow('dividends', 'Dividends', 'coins'),
    lit(
      {
        key: '/screener/advanced',
        icon: 'table',
        label: 'Advanced',
        link: linkOptions({ to: '/screener/advanced' }),
      },
      pathname === '/screener/advanced',
    ),
    soonRow('custom', 'Custom', 'terminal'),
  ]
  return { visible: rows.slice(0, SCREENER_VISIBLE), more: rows.slice(SCREENER_VISIBLE) }
}

/* PORTFOLIO — `/portfolio/*` (every Portfolio frame, 5196:53349). The six
   tabs that were in-page pills, plus Health checkup. My Dashboards was a row
   here in round one; it is a section of its own now. */

/** rest:pa — Portfolio pages with no row of their own light the row they were opened from, in
    the sidebar and in the section strip they keep: Import review → Overview (mock
    `#/portfolio/import`), Add transaction's Mitra AI page → Activity (mock `sideActive`). */
const LIT_FOR: Readonly<Record<string, string>> = {
  '/portfolio/import': '/portfolio',
  '/portfolio/add/ai': '/portfolio/activity',
}

const portfolioRowKey = (pathname: string): string => LIT_FOR[pathname] ?? pathname

const PORTFOLIO_ROWS: readonly PageSpec[] = [
  { key: '/portfolio', icon: 'market', label: 'Overview', link: linkOptions({ to: '/portfolio' }) },
  {
    key: '/portfolio/holdings',
    icon: 'clipboard',
    label: 'Holdings',
    link: linkOptions({ to: '/portfolio/holdings' }),
  },
  {
    key: '/portfolio/allocation',
    icon: 'pie',
    label: 'Allocation',
    link: linkOptions({ to: '/portfolio/allocation' }),
  },
  {
    key: '/portfolio/activity',
    icon: 'receipt',
    label: 'Activity',
    link: linkOptions({ to: '/portfolio/activity' }),
  },
  {
    key: '/portfolio/income',
    icon: 'coins',
    label: 'Income',
    link: linkOptions({ to: '/portfolio/income' }),
  },
  {
    key: '/portfolio/analytics',
    icon: 'percent',
    label: 'Analytics',
    link: linkOptions({ to: '/portfolio/analytics' }),
  },
  {
    key: '/portfolio/health',
    icon: 'gauge',
    label: 'Health Checkup',
    link: linkOptions({ to: '/portfolio/health' }),
  },
]

/* ACCOUNT — `/profile`, `/settings?tab=`, `/subscription/*`, `/payment/*`
   (all 36 Account frames), a context section on top of the list while one of
   them is open. The entries of the old in-page AccountTabs strip, in its
   order, less Linked Accounts (rest:ab 2026-10-29: the mock has no such page and mm-be no link
   to back it). A bare `/settings` opens Edit Profile, so that row lights there. */

const SETTINGS_TABS = [
  'edit-profile',
  'notifications',
  'appearance',
  'security',
  'privacy',
  'help',
] as const

type SettingsTab = (typeof SETTINGS_TABS)[number]

function accountSection(location: NavLocation): NavSection {
  const { pathname } = location
  const settingsTab =
    pathname === '/settings' ? currentOf(location, 'tab', SETTINGS_TABS, 'edit-profile') : null
  const settings = (tab: SettingsTab, label: string, icon: SoulName) =>
    lit({ key: tab, label, icon, link: settingsLink(tab) }, tab === settingsTab)

  return {
    kind: 'pages',
    id: 'account',
    context: true,
    label: 'Account',
    icon: 'users',
    front: linkOptions({ to: '/profile' }),
    current: true,
    pages: allVisible([
      lit(
        { key: 'account', label: 'Account', icon: 'users', link: linkOptions({ to: '/profile' }) },
        pathname === '/profile',
      ),
      settings('edit-profile', 'Edit Profile', 'idcard'),
      lit(
        { key: 'plan', label: 'Plan', icon: 'tag', link: linkOptions({ to: '/subscription' }) },
        // The whole buying flow is the Plan row's: checkout, manage, billing
        // and the gateway's return pages.
        withinPrefix(pathname, '/subscription') || withinPrefix(pathname, '/payment'),
      ),
      settings('notifications', 'Notifications', 'bell'),
      settings('appearance', 'Appearance', 'range'),
      settings('security', 'Security', 'vault'),
      settings('privacy', 'Privacy', 'shield'),
      settings('help', 'Help', 'book'),
    ]),
  }
}

/* BROKER ANALYSIS — `/brokers/*` (4236:70217 and its 23 siblings). The
   revamp mock's list and split (owner D7, 2026-10-27): Overview, All
   Brokers, By Stock (the app's old "Stock insight" tab), By Broker (the old
   "Broker's Choice"), Reviews, Most Traded, Sector Flow, Large Holdings, then
   "Show 4 more". Last Minute Movers is a live row and tab (owner ruling 1 + 2,
   2026-10-28): its page is built to the mock behind `LAST_MINUTE_AVAILABLE`, because mm-be
   serves no feed for it yet (routes/brokers.ts).

   By Stock, By Broker, Reviews and PV Range each need a stock or a broker the
   URL may not carry. Each opens the entity in view when there is one, else
   the member's last-used stock/broker or a market-wide default, via a
   param-less route (`ByStockRedirect.tsx`, `PVRangeRedirect.tsx`,
   `ByBrokerRedirect.tsx`, `ReviewsRedirect.tsx`, `routes/brokers.ts`) that
   resolves it and redirects (owner, 2026-09-25). */

const BROKER_PAGES: ReadonlySet<string> = new Set([
  'all',
  'by-stock',
  'by-broker',
  'reviews',
  'most-traded',
  'sector-flow',
  'large-trades',
  'last-minute',
  'large-holdings',
  'pv-range',
  'compare',
])

function brokersPages(pathname: string): Paged<PageRow> {
  const [first, page, entity] = segmentsOf(pathname)
  const here = first === 'brokers'
  const symbol = here && (page === 'by-stock' || page === 'pv-range') ? entity : undefined
  // Any other second segment is a broker code: `/brokers/58` is By Broker's
  // page, and `/brokers/58/reviews` is Reviews'.
  const code = here && page !== undefined && !BROKER_PAGES.has(page) ? page : undefined
  const current = !here
    ? null
    : page === undefined
      ? 'overview'
      : code === undefined
        ? page
        : entity === 'reviews'
          ? 'reviews'
          : 'by-broker'

  const pages: Paged<PageSpec> = {
    visible: [
      { key: 'overview', icon: 'market', label: 'Overview', link: linkOptions({ to: '/brokers' }) },
      {
        key: 'all',
        icon: 'handshake',
        label: 'All Brokers',
        link: linkOptions({ to: '/brokers/all' }),
      },
      {
        key: 'by-stock',
        icon: 'compare',
        label: 'By Stock',
        link:
          symbol === undefined ? linkOptions({ to: '/brokers/by-stock' }) : brokerStockLink(symbol),
      },
      {
        key: 'by-broker',
        icon: 'brokers-mitra',
        label: 'By Broker',
        link: code === undefined ? linkOptions({ to: '/brokers/by-broker' }) : brokerLink(code),
      },
      {
        key: 'reviews',
        icon: 'users',
        label: 'Reviews',
        link:
          code === undefined ? linkOptions({ to: '/brokers/reviews' }) : brokerReviewsLink(code),
      },
      {
        key: 'most-traded',
        icon: 'movers',
        label: 'Most Traded',
        link: linkOptions({ to: '/brokers/most-traded' }),
      },
      {
        key: 'sector-flow',
        icon: 'pie',
        label: 'Sector Flow',
        link: linkOptions({ to: '/brokers/sector-flow' }),
      },
      {
        key: 'large-holdings',
        icon: 'vault',
        label: 'Large Holdings',
        link: linkOptions({ to: '/brokers/large-holdings' }),
      },
    ],
    more: [
      {
        key: 'large-trades',
        icon: 'ingot',
        label: 'Large Trades',
        link: linkOptions({ to: '/brokers/large-trades' }),
      },
      {
        key: 'last-minute',
        icon: 'pulse',
        label: 'Last Minute Movers',
        link: linkOptions({ to: '/brokers/last-minute' }),
      },
      {
        key: 'pv-range',
        icon: 'range',
        label: 'PV Range',
        link: symbol === undefined ? linkOptions({ to: '/brokers/pv-range' }) : pvRangeLink(symbol),
      },
      {
        key: 'compare',
        icon: 'compare',
        label: 'Compare',
        link: linkOptions({ to: '/brokers/compare' }),
      },
    ],
  }

  return litPages(pages, (row) => lit(row, row.key === current))
}

/* MUTUAL FUNDS — `/mutual-funds/*`. Four rows, then "Show 1 more" with Dividends
   (`4790:18605`): Monthly Holdings went into Industry Holdings' "All sectors" (the 2026-11-03
   mock), and More stays (owner-locked). Each page's tabs, period and view live in its own search
   params, so a row lights on its path alone, whatever the search says. A fund's own page
   (`/mutual-funds/fund/$symbol`) has no row: it lights Fund Rankings, as the mock does. */

const FUND_PAGE_PREFIX = '/mutual-funds/fund/'

/** The funds row the page on screen lights: its own, or Fund Rankings for one fund's page. */
const fundsRowKey = (pathname: string): string =>
  pathname.startsWith(FUND_PAGE_PREFIX) ? '/mutual-funds/rankings' : pathname

const FUNDS_PAGES: Paged<PageSpec> = {
  visible: [
    {
      key: '/mutual-funds',
      icon: 'percent',
      label: 'Analytics',
      link: linkOptions({ to: '/mutual-funds' }),
    },
    {
      key: '/mutual-funds/price-vs-nav',
      icon: 'compare',
      label: 'Price vs NAV',
      link: linkOptions({ to: '/mutual-funds/price-vs-nav' }),
    },
    {
      key: '/mutual-funds/rankings',
      icon: 'table',
      label: 'Fund Rankings',
      link: linkOptions({ to: '/mutual-funds/rankings' }),
    },
    {
      key: '/mutual-funds/industry-holdings',
      icon: 'pie',
      label: 'Industry Holdings',
      link: linkOptions({ to: '/mutual-funds/industry-holdings' }),
    },
  ],
  more: [
    {
      key: '/mutual-funds/dividends',
      icon: 'coin',
      label: 'Dividends',
      link: linkOptions({ to: '/mutual-funds/dividends' }),
    },
  ],
}

/* PUBLIC ISSUES — `/market/issues?type=` (owner D1, 2026-10-27, reversing the
   2026-10-12 plain row): one row per issue type mm-be serves, in the mock's
   order, lit by `?type=` on the list alone — a bare list shows IPOs, so IPO
   lights there. IPO-Local (owner P5, 2026-10-29) is the mock's fifth row: mm-be does not
   serve `type=local` yet, so its page says "Not available yet" (`ipoAvailability.ts`). On an
   unknown value in a hand-typed URL no row lights, as on an issue's own `/ipo/$key` page. */

const ISSUE_ROWS: readonly { type: IssueListType; label: string; icon: SoulName }[] = [
  { type: 'ipo', label: 'IPO', icon: 'ticket' },
  { type: 'fpo', label: 'FPO', icon: 'ticket' },
  { type: 'right', label: 'Right', icon: 'certificate' },
  { type: 'auction', label: 'Auction', icon: 'megaphone' },
  { type: 'local', label: 'IPO-Local', icon: 'ticket' },
  { type: 'mutual_fund', label: 'Mutual Fund', icon: 'coins' },
  { type: 'bonds', label: 'Bonds', icon: 'bank' },
  { type: 'qii', label: 'IPO for QII', icon: 'vault' },
  { type: 'migrant', label: 'Migrant Workers', icon: 'globe' },
]

const ISSUES_VISIBLE = 5

function issuesPages(location: NavLocation): Paged<PageRow> {
  const shown =
    location.pathname === '/market/issues'
      ? currentOf(
          location,
          'type',
          ISSUE_ROWS.map((row) => row.type),
          'ipo',
        )
      : null
  const rows = ISSUE_ROWS.map(({ type, label, icon }) =>
    lit({ key: type, label, icon, link: issuesLink(type) }, type === shown),
  )
  // Five rows, then "Show 3 more": every sidebar group keeps More (owner-locked 2026-10-12).
  return { visible: rows.slice(0, ISSUES_VISIBLE), more: rows.slice(ISSUES_VISIBLE) }
}

/* BASKETS — `/baskets?audience=` (the 2026-11-03 mock's two rows, which replace the list's tabs):
   For Investors (a bare `/baskets`) and For Traders, lit by `?audience=` on the list alone. A
   basket's own page lights neither, as an issue's `/ipo/$key` page lights no issue row. Two rows,
   so nothing to put behind More. The mock's Screener and Compare rows are sections of their own
   here (Screener; the stock Compare page). */

const BASKET_ROWS: readonly { audience: Audience; label: string; icon: SoulName }[] = [
  { audience: 'investor', label: 'For Investors', icon: 'basket' },
  { audience: 'trader', label: 'For Traders', icon: 'pulse' },
]

function basketsPages(location: NavLocation): Paged<PageRow> {
  const shown =
    location.pathname === '/baskets'
      ? currentOf(
          location,
          'audience',
          BASKET_ROWS.map((row) => row.audience),
          'investor',
        )
      : null
  return allVisible(
    BASKET_ROWS.map(({ audience, label, icon }) =>
      lit(
        {
          key: audience,
          label,
          icon,
          link:
            audience === 'investor'
              ? linkOptions({ to: '/baskets' })
              : linkOptions({ to: '/baskets', search: { audience } }),
        },
        audience === shown,
      ),
    ),
  )
}

/* WATCHLIST — `/watchlist?list=` (4236:113812), a context section on top of
   the list while the page is open: it is the page's only list switcher. One
   row per list the member keeps, in the order mm-be returns them; the first
   is lit when the URL names none, or names a list that no longer exists —
   the list the screen falls back to showing. */

export interface WatchlistRef {
  readonly id: string
  readonly name: string
}

/** The member's lists as rows, or `null` while they are still loading. */
export function watchlistPages(
  location: NavLocation,
  lists: Loadable<readonly WatchlistRef[]>,
): Paged<LinkRow> | null {
  if (lists.status === 'pending') return null

  const [first] = lists.status === 'ready' ? lists.data : []
  if (lists.status === 'failed' || first === undefined) {
    // No names to draw — the screen is saying why. One row for the page
    // itself keeps "the section holds the row you are on" true.
    return allVisible([
      lit(
        {
          key: 'watchlist',
          label: 'Watchlist',
          icon: 'bookmark',
          link: linkOptions({ to: '/watchlist' }),
        },
        true,
      ),
    ])
  }

  const named = searchText(location.search, 'list')
  const current = lists.data.find((list) => list.id === named) ?? first
  return allVisible(
    lists.data.map((list) =>
      lit(
        { key: list.id, label: list.name, icon: 'bookmark', link: watchlistLink(list.id) },
        list.id === current.id,
      ),
    ),
  )
}

/* ALERTS — `/alerts?status=` (4236:109443). "Active · 3", "Expired · 2": the
   counts are the live lists' own lengths, and a count still loading (or
   failed) is left off rather than shown as a number nobody measured. A row
   lights only on the Alerts page itself. */

export interface AlertCounts {
  readonly active?: number
  readonly expired?: number
}

const ALERT_STATUSES = ['active', 'expired'] as const

type AlertStatus = (typeof ALERT_STATUSES)[number]

export function alertsPages(location: NavLocation, counts: AlertCounts): Paged<LinkRow> {
  const current =
    location.pathname === '/alerts' ? currentOf(location, 'status', ALERT_STATUSES, 'active') : null
  const statusRow = (status: AlertStatus, label: string, icon: SoulName, count?: number) =>
    lit(
      {
        key: status,
        label,
        icon,
        ...(count === undefined ? {} : { count }),
        link: alertsLink(status),
      },
      status === current,
    )
  return allVisible([
    statusRow('active', 'Active', 'ghanta', counts.active),
    statusRow('expired', 'Expired', 'bell', counts.expired),
  ])
}

/**
 * Every section for the URL, in the order the sidebar draws them: the page's
 * own context section first (the Account pages, the member's watchlists) when
 * there is one, then Home, then the three groups (revamp mock, 2026-10-26) —
 * with a stock's own section right under Market, the section a stock belongs
 * to in the bars (owner, 2026-10-23). A group's sections run consecutively,
 * so the renderer draws a label wherever `group` changes.
 *
 * Public Issues is a section of issue types (owner D1, 2026-10-27), current
 * on the list and on an issue's own `/ipo/$key` page. Technical Chart opens
 * the chart workspace in a tab of its own, as every chart entry point does,
 * so it never lights. Nor does Mitra AI from the URL: the renderer lights it
 * while the panel is open. It is a launcher, so a build that cannot reach the
 * engine draws no Mitra AI row at all (`config.mitraLaunchersEnabled`,
 * launcher-gate.test.tsx), and the rail and drawer drop it for a reader outside
 * Mitra's rollout (`useOpenSections`). Baskets is For Investors and For Traders (2026-11-03 mock);
 * Screener opens on Advanced, its one live page, beside five Soon rows (owner, 2026-10-30).
 */
export function navSections(location: NavLocation): readonly NavSection[] {
  const { pathname } = location
  const here = currentSectionOf(pathname)
  const isHere = (id: NavSectionId) => here === id

  const context =
    here === 'account'
      ? accountSection(location)
      : here === 'watchlist'
        ? ({
            kind: 'watchlist',
            id: 'watchlist',
            context: true,
            label: 'Watchlist',
            icon: 'bookmark',
            front: linkOptions({ to: '/watchlist' }),
            current: true,
          } satisfies NavSection)
        : null
  const stock = here === 'stock' ? stockSection(location) : null

  const sections: NavSection[] = [
    {
      kind: 'link',
      id: 'home',
      label: 'Home',
      icon: 'home',
      front: linkOptions({ to: '/' }),
      current: isHere('home'),
    },
    {
      kind: 'pages',
      id: 'market',
      group: 'research',
      label: 'Market',
      icon: 'market',
      front: linkOptions({ to: '/market' }),
      current: isHere('market'),
      pages: litPages(MARKET_PAGES, marketRowLit(pathname)),
    },
    ...(stock === null ? [] : [stock]),
    {
      kind: 'pages',
      id: 'brokers',
      group: 'research',
      label: 'Broker Analysis',
      icon: 'handshake',
      front: linkOptions({ to: '/brokers' }),
      current: isHere('brokers'),
      pages: brokersPages(pathname),
    },
    {
      kind: 'pages',
      id: 'funds',
      group: 'research',
      label: 'Mutual Funds',
      icon: 'coins',
      front: linkOptions({ to: '/mutual-funds' }),
      current: isHere('funds'),
      pages: litPages(FUNDS_PAGES, (row) => lit(row, row.key === fundsRowKey(pathname))),
    },
    {
      kind: 'pages',
      id: 'signals',
      group: 'research',
      label: 'Signal Zone',
      icon: 'signal-zone',
      front: linkOptions({ to: '/market/signals' }),
      current: isHere('signals'),
      pages: litPages(SIGNALS_PAGES, (row) => lit(row, row.key === signalsRowKey(location))),
    },
    {
      kind: 'pages',
      id: 'issues',
      group: 'research',
      label: 'Public Issues',
      icon: 'ticket',
      front: linkOptions({ to: '/market/issues' }),
      current: isHere('issues'),
      pages: issuesPages(location),
    },
    // Soon (owner, 2026-10-28): in the mock's Research group, after Public
    // Issues. No route or backend page exists, so it has no family either.
    {
      kind: 'soon',
      id: 'commodities',
      group: 'research',
      label: 'Commodities',
      icon: 'ingot',
      current: false,
    },
    {
      kind: 'pages',
      id: 'portfolio',
      group: 'money',
      label: 'Portfolio',
      icon: 'pie',
      front: linkOptions({ to: '/portfolio' }),
      current: isHere('portfolio'),
      pages: allVisible(
        PORTFOLIO_ROWS.map((row) => lit(row, row.key === portfolioRowKey(pathname))),
      ),
    },
    {
      kind: 'alerts',
      id: 'alerts',
      group: 'money',
      label: 'Alerts',
      icon: 'ghanta',
      front: linkOptions({ to: '/alerts' }),
      current: isHere('alerts'),
    },
    ...(config.mitraLaunchersEnabled
      ? [
          {
            kind: 'mitra',
            id: 'mitra',
            group: 'tools',
            label: 'Mitra AI',
            icon: 'terminal',
            front: linkOptions({ to: '/chat' }),
            current: false,
          } satisfies NavSection,
        ]
      : []),
    // The reader's own dashboards: their own section rather than a Portfolio
    // page (owner, 2026-10-18), since one can be over a watchlist or the
    // whole market, not only the book. `/portfolio/dashboard` redirects here.
    {
      kind: 'link',
      id: 'dashboards',
      group: 'tools',
      label: 'Analytics Studio',
      icon: 'clipboard',
      front: linkOptions({ to: '/dashboards' }),
      current: isHere('dashboards'),
    },
    {
      kind: 'pages',
      id: 'screener',
      group: 'tools',
      label: 'Screener',
      icon: 'sliders',
      front: linkOptions({ to: '/screener/advanced' }),
      current: isHere('screener'),
      pages: screenerPages(pathname),
    },
    {
      kind: 'pages',
      id: 'baskets',
      group: 'tools',
      label: 'Baskets',
      icon: 'basket',
      front: linkOptions({ to: '/baskets' }),
      current: isHere('baskets'),
      pages: basketsPages(location),
    },
    {
      kind: 'link',
      id: 'chart',
      group: 'tools',
      label: 'Technical Chart',
      icon: 'candles',
      front: linkOptions({ to: '/chart' }),
      current: false,
      newTab: true,
    },
    {
      kind: 'pages',
      id: 'learn',
      group: 'tools',
      label: 'Learn',
      icon: 'learn',
      front: linkOptions({ to: '/courses/path' }),
      current: isHere('learn'),
      pages: learnPages(pathname),
    },
  ]

  return context === null ? sections : [context, ...sections]
}

/* SECTION TAB STRIP — the page's own section's pages across the top of the
   main column (pages revamp, 2026-10-27; SectionTabs.tsx, drawn by
   ScreenLayout). Read off the same sections as the sidebar, so the two can
   never list different pages. */

/** The strip's model: whose pages, and every one of them in the sidebar's order. */
export interface SectionTabsModel {
  readonly id: NavSectionId
  /** `alerts` counts its rows from the member's lists (useAlertCounts). */
  readonly kind: 'pages' | 'alerts'
  /** The section's name: the strip is the "{label} pages" navigation. */
  readonly label: string
  /** The shown pages, then the ones behind "Show N more" — the strip has no split. */
  readonly tabs: readonly PageRow[]
}

/**
 * A row's own path: its route with its params filled in. What a row IS, as
 * against where it lights — Courses lights on a course's page too, but only
 * `/courses` is Courses.
 */
function pathOf(link: NavLink): string {
  const params: Readonly<Record<string, string>> = 'params' in link ? link.params : {}
  return link.to.replace(/\$(\w+)/g, (_match, name: string) => params[name] ?? '')
}

const pathnameOf = (location: NavLocation) => `/${segmentsOf(location.pathname).join('/')}`

/**
 * Pages in a section with no tab of their own that still draw its strip, none lit, as the mock
 * does: Gainers & Losers, which the sidebar no longer lists (owner D2, 2026-10-27) but the Top
 * Gainers shortcut and old links still reach; Stock Prices, reached by link (a stock's "Browse
 * stock prices", the shortcuts picker) rather than from the sidebar or the strip; and Market
 * update's News tab and a News article, reached from Market update's own tabs and the rail.
 */
const STRIP_WITHOUT_TAB: ReadonlySet<string> = new Set([
  '/market/gainers-losers',
  '/market/stock-prices',
  '/market/news',
  '/market/news/article',
])

/**
 * The strip for the URL, or `null` where none is drawn. One is drawn for the
 * section the page is in when it has two pages or more and the page on screen
 * IS one of them: one lit row, whose own path is the page's. So a detail page
 * — a course, an issue, a newsletter, a stock's Signal page — keeps its own
 * title and back button, though its row lights in the sidebar (a newsletter issue excepted: see
 * `onPage`). Never for the
 * page's own context section (a stock has its own strip in the page, Account
 * and Watchlist none), a plain row (Home, Analytics Studio), Mitra AI or a
 * Soon section. Pure, so the strip never waits on anything but the Alerts
 * counts, which `counts` fills in when they land.
 */
export function sectionTabs(
  location: NavLocation,
  counts: AlertCounts = {},
): SectionTabsModel | null {
  const section = navSections(location).find(
    (candidate) => candidate.current && candidate.context !== true,
  )
  if (section === undefined) return null

  let tabs: readonly PageRow[]
  if (section.kind === 'pages') tabs = [...section.pages.visible, ...section.pages.more]
  else if (section.kind === 'alerts') tabs = alertsPages(location, counts).visible
  else return null

  const linked = tabs.filter((row): row is LinkRow => row.soon !== true)
  const [page, ...alsoLit] = linked.filter((row) => row.active)
  if (linked.length < 2 || alsoLit.length > 0) return null
  const pathname = pathnameOf(location)
  if (page === undefined) {
    // A page the section owns but its strip leaves out draws the strip with no tab lit.
    if (!STRIP_WITHOUT_TAB.has(pathname)) return null
  } else {
    // A newsletter issue keeps the strip (the revamp mock draws it above the reader's own back
    // button), so Newsletters is the page on any path under it. A stock's Signal page and its
    // Technicals keep it too, the board they were opened from lit (sz:sa, mock `#/signals/signal`).
    const onPage =
      pathOf(page.link) === pathname ||
      LIT_FOR[pathname] === page.key ||
      (page.key === NEWSLETTERS && withinPrefix(pathname, NEWSLETTERS)) ||
      (section.id === 'signals' && pathname.startsWith(SIGNAL_STOCK_PREFIX)) ||
      (section.id === 'learn' && isLearnDrillDown(pathname))
    if (!onPage) return null
  }

  return { id: section.id, kind: section.kind, label: section.label, tabs }
}
